import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import useTranslation from "../hooks/useTranslation";
import { TEXT_LANGUAGES } from "../constants";
import { XMarkIcon } from "@heroicons/react/24/outline";

export const Route = createFileRoute("/start-learning")({
  component: StartLearningOnboardingPage,
});

type OnboardingStep =
  | "language"
  | "level"
  | "mainCharacter"
  | "textType"
  | "interest"
  | "preview";

const LEVEL_OPTIONS = ["A0", "A1", "A2", "B1", "B2", "C1", "C2"];
const TEXT_TYPE_OPTIONS = ["STORY", "DIALOGUE", "CRIME", "PODCAST", "KNOWLEDGE"] as const;
const INTEREST_EXAMPLES = [
  "onboarding_interest_example_midnight_bookshop",
  "onboarding_interest_example_hidden_city_routes",
  "onboarding_interest_example_startup_after_failure",
  "onboarding_interest_example_kitchen_science",
  "onboarding_interest_example_train_window_stories",
] as const;
const CHARACTER_PRESETS = [
  "onboarding_main_character_first_person",
  "onboarding_main_character_traveler",
  "onboarding_main_character_student",
  "onboarding_main_character_local_friend",
  "onboarding_main_character_curious_explorer",
] as const;

const STEP_SEQUENCE: OnboardingStep[] = [
  "language",
  "level",
  "mainCharacter",
  "textType",
  "interest",
  "preview",
];

function StartLearningOnboardingPage() {
  return <StartLearningOnboardingFlow />;
}

export function StartLearningOnboardingFlow({
  onClose,
  embedded = false,
}: {
  onClose?: () => void;
  embedded?: boolean;
}) {
  const { translate } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [languageQuery, setLanguageQuery] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [levelByLanguage, setLevelByLanguage] = useState<Record<string, string>>({});
  const [mainCharacter, setMainCharacter] = useState("");
  const [customCharacter, setCustomCharacter] = useState("");
  const [selectedTextType, setSelectedTextType] = useState<string>("");
  const [customContentInterest, setCustomContentInterest] = useState("");

  const currentStep = STEP_SEQUENCE[stepIndex];
  const sortedLanguages = useMemo(
    () =>
      [...TEXT_LANGUAGES].sort((a, b) =>
        translate(a.prettyTranslationKey).localeCompare(
          translate(b.prettyTranslationKey),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [translate],
  );

  const selectedLanguageLabels = useMemo(
    () =>
      selectedLanguages
        .map((id) => sortedLanguages.find((lang) => lang.id === id))
        .filter((lang): lang is (typeof TEXT_LANGUAGES)[number] => Boolean(lang))
        .map((lang) => translate(lang.prettyTranslationKey)),
    [selectedLanguages, sortedLanguages, translate],
  );
  const selectedLanguagesSorted = useMemo(
    () =>
      selectedLanguages
        .map((id) => sortedLanguages.find((lang) => lang.id === id))
        .filter((lang): lang is (typeof TEXT_LANGUAGES)[number] => Boolean(lang))
        .map((lang) => lang.id),
    [selectedLanguages, sortedLanguages],
  );
  const filteredLanguages = useMemo(() => {
    const query = languageQuery.trim().toLowerCase();
    if (!query) {
      return sortedLanguages;
    }
    return sortedLanguages.filter((language) => {
      const label = translate(language.prettyTranslationKey).toLowerCase();
      return (
        label.includes(query) ||
        language.short.toLowerCase().includes(query) ||
        language.id.toLowerCase().includes(query)
      );
    });
  }, [languageQuery, sortedLanguages, translate]);

  const resolvedMainCharacter = customCharacter.trim() || mainCharacter;

  const canAdvance =
    (currentStep === "language" && selectedLanguages.length > 0) ||
    (currentStep === "level" &&
      selectedLanguages.every((language) => Boolean(levelByLanguage[language]))) ||
    (currentStep === "mainCharacter" && Boolean(resolvedMainCharacter)) ||
    (currentStep === "textType" && Boolean(selectedTextType)) ||
    currentStep === "interest" ||
    currentStep === "preview";

  const goNext = () => {
    if (!canAdvance) {
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, STEP_SEQUENCE.length - 1));
  };

  const goBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const toggleLanguage = (languageId: string) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(languageId)) {
        const next = prev.filter((id) => id !== languageId);
        setLevelByLanguage((current) => {
          const copy = { ...current };
          delete copy[languageId];
          return copy;
        });
        return next;
      }
      return [...prev, languageId];
    });
  };

  const selectTextType = (textType: string) => {
    setSelectedTextType(textType);
  };

  const outerWrapperClass = embedded
    ? "min-h-0 bg-transparent"
    : "min-h-screen bg-[#f6f7fb]";
  const innerWrapperClass = embedded
    ? "mx-auto flex h-full w-full max-w-none items-stretch sm:max-w-3xl sm:items-start"
    : "mx-auto flex min-h-screen w-full max-w-3xl items-start px-4 pb-8 pt-6 sm:px-6 lg:items-center lg:py-8";
  const onboardingShellClass = embedded
    ? "flex h-full min-h-[100dvh] w-full flex-col border-0 bg-white px-4 py-4 sm:h-[760px] sm:min-h-[620px] sm:rounded-3xl sm:border sm:border-slate-200 sm:p-6"
    : "flex h-[78vh] min-h-[620px] w-full flex-col rounded-3xl border border-slate-200 bg-white p-4 sm:h-[760px] sm:p-6";

  return (
    <div className={outerWrapperClass}>
      <div className={innerWrapperClass}>
        <div data-testid="onboarding-shell" className={onboardingShellClass}>
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              In Sprachwelt eintauchen
            </p>
            <div className="flex items-center gap-2">
              <p className="rounded-full border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
                {stepIndex + 1}/{STEP_SEQUENCE.length}
              </p>
              {onClose ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
                  aria-label={translate("onboarding_modal_close")}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 pt-5">
            {currentStep === "language" && (
              <StepShell
                title={translate("onboarding_language_title")}
                subtitle={translate("onboarding_language_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl">
                  <div className="flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2">
                    <input
                      type="text"
                      value={languageQuery}
                      onFocus={() => setIsLanguageMenuOpen(true)}
                      onChange={(event) => {
                        setLanguageQuery(event.target.value);
                        setIsLanguageMenuOpen(true);
                      }}
                      placeholder={translate("onboarding_language_dropdown_placeholder")}
                      className="w-full border-0 bg-transparent text-base text-slate-800 placeholder:text-slate-500 focus:outline-hidden"
                    />
                    <button
                      type="button"
                      onClick={() => setIsLanguageMenuOpen((prev) => !prev)}
                      className="ml-2 text-slate-500"
                      aria-label={translate("onboarding_language_menu_toggle")}
                    >
                      ▾
                    </button>
                  </div>
                  {isLanguageMenuOpen && (
                    <div className="mt-2 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2">
                      {filteredLanguages.map((language) => {
                        const label = translate(language.prettyTranslationKey);
                        const selected = selectedLanguages.includes(language.id);
                        return (
                          <label
                            key={language.id}
                            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={selected}
                              onChange={() => toggleLanguage(language.id)}
                            />
                            <span>{label}</span>
                            <span className="ml-auto inline-flex h-6 min-w-8 items-center justify-center rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-semibold text-slate-600">
                              {resolveLanguageBadge(language.id)}
                            </span>
                          </label>
                        );
                      })}
                      {filteredLanguages.length === 0 && (
                        <p className="px-2 py-2 text-sm text-slate-500">
                          {translate("onboarding_language_search_empty")}
                        </p>
                      )}
                    </div>
                  )}
                  {selectedLanguageLabels.length > 0 && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        {translate("onboarding_language_selected_label")}
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {selectedLanguageLabels.map((label) => (
                          <span
                            key={label}
                            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </StepShell>
            )}

            {currentStep === "level" && (
              <StepShell
                title={translate("onboarding_level_title")}
                subtitle={translate("onboarding_level_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl space-y-3">
                  {selectedLanguagesSorted.map((languageId) => {
                    const language = sortedLanguages.find((entry) => entry.id === languageId);
                    if (!language) {
                      return null;
                    }
                    return (
                      <div
                        key={languageId}
                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3"
                      >
                        <label className="mb-2 block text-sm font-semibold text-slate-700">
                          <span className="inline-flex w-full items-center justify-between gap-2">
                            {translate("onboarding_level_for_language", {
                              language: translate(language.prettyTranslationKey),
                            })}
                            <span className="inline-flex h-6 min-w-8 items-center justify-center rounded-md border border-slate-200 bg-white px-1.5 text-[11px] font-semibold text-slate-600">
                              {resolveLanguageBadge(language.id)}
                            </span>
                          </span>
                        </label>
                        <select
                          value={levelByLanguage[languageId] ?? ""}
                          onChange={(event) =>
                            setLevelByLanguage((prev) => ({
                              ...prev,
                              [languageId]: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-800"
                        >
                          <option value="">{translate("onboarding_level_dropdown_placeholder")}</option>
                          {LEVEL_OPTIONS.map((level) => (
                            <option key={level} value={level}>
                              {`${level} · ${translate(`onboarding_level_${level.toLowerCase()}`)}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </StepShell>
            )}

            {currentStep === "mainCharacter" && (
              <StepShell
                title={translate("onboarding_main_character_title")}
                subtitle={translate("onboarding_main_character_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {CHARACTER_PRESETS.map((key, index) => {
                      const label = translate(key);
                      const selected = mainCharacter === label && !customCharacter.trim();
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setMainCharacter(label);
                            setCustomCharacter("");
                          }}
                          className={`rounded-2xl border p-3 text-left ${
                            selected
                              ? "border-sky-300 bg-sky-50"
                              : "border-slate-200 bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#e8eef7_100%)] text-lg">
                              {resolveCharacterIcon(index)}
                            </div>
                            <p className="text-sm leading-tight font-semibold text-slate-800">
                              {label}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 px-1">
                    <div className="h-px flex-1 bg-slate-200" />
                    <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      {translate("onboarding_custom_character_separator")}
                    </p>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      {translate("onboarding_custom_character_label")}
                    </label>
                    <input
                      type="text"
                      value={customCharacter}
                      onChange={(event) => setCustomCharacter(event.target.value)}
                      placeholder={translate("onboarding_custom_character_placeholder")}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-800"
                    />
                  </div>
                </div>
              </StepShell>
            )}

            {currentStep === "textType" && (
              <StepShell
                title={translate("onboarding_text_type_title")}
                subtitle={translate("onboarding_text_type_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {TEXT_TYPE_OPTIONS.map((type) => {
                    const selected = selectedTextType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => selectTextType(type)}
                        className={`rounded-2xl border p-3 text-left ${
                          selected
                            ? "border-sky-300 bg-sky-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#e8eef7_100%)] text-lg">
                            {resolveTextTypeIcon(type)}
                          </div>
                          <p className="text-sm leading-tight font-semibold text-slate-800">
                            {translate(`text_type_${type.toLowerCase()}`)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                  </div>
                </div>
              </StepShell>
            )}

            {currentStep === "interest" && (
              <StepShell
                title={translate("onboarding_interest_title")}
                subtitle={translate("onboarding_interest_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl space-y-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      {translate("onboarding_custom_content_interest_label")}
                    </label>
                    <input
                      type="text"
                      value={customContentInterest}
                      onChange={(event) => setCustomContentInterest(event.target.value)}
                      placeholder={translate("onboarding_custom_content_interest_placeholder")}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-800"
                    />
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                      {translate("onboarding_interest_examples_label")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {INTEREST_EXAMPLES.map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setCustomContentInterest(translate(key))}
                          className="rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700"
                        >
                          {translate(key)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </StepShell>
            )}

            {currentStep === "preview" && (
              <StepShell
                title={translate("onboarding_preview_title")}
                subtitle={translate("onboarding_preview_subtitle")}
              >
                <div className="mx-auto w-full max-w-xl rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-900">{translate("onboarding_summary_languages")}:</span>{" "}
                    {selectedLanguageLabels.join(", ")}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold text-slate-900">{translate("onboarding_summary_levels")}:</span>{" "}
                    {selectedLanguagesSorted
                      .map((languageId) => {
                        const language = sortedLanguages.find((entry) => entry.id === languageId);
                        if (!language) {
                          return null;
                        }
                        return `${translate(language.prettyTranslationKey)} (${levelByLanguage[languageId]})`;
                      })
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold text-slate-900">{translate("onboarding_summary_character")}:</span>{" "}
                    {resolvedMainCharacter}
                  </p>
                  <p className="mt-2">
                    <span className="font-semibold text-slate-900">{translate("onboarding_summary_text_types")}:</span>{" "}
                    {[
                      ...(selectedTextType
                        ? [translate(`text_type_${selectedTextType.toLowerCase()}`)]
                        : []),
                      ...(customContentInterest.trim()
                        ? [customContentInterest.trim()]
                        : []),
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </StepShell>
            )}
          </div>

          <div className="mt-5 border-t border-slate-200 pt-3">
            {currentStep !== "preview" && (
              <p className="mb-3 text-center text-sm text-slate-600">
                {translate("onboarding_change_anytime_hint")}
              </p>
            )}
            <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={stepIndex === 0}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40"
            >
              {translate("onboarding_back")}
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance}
              className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-white disabled:opacity-60"
            >
              {currentStep === "preview"
                ? translate("onboarding_finish")
                : translate("onboarding_continue")}
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function StepShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <section className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <h1 className="text-balance text-center font-serif text-2xl leading-tight text-slate-900 sm:text-3xl">
          {title}
        </h1>
        <p className="mx-auto mt-2 max-w-2xl text-balance text-center text-sm text-slate-600 sm:text-base">
          {subtitle}
        </p>
      </div>
      <div className="mt-4 min-h-0 flex-1 pr-1">{children}</div>
    </section>
  );
}

function resolveTextTypeIcon(textType: (typeof TEXT_TYPE_OPTIONS)[number]) {
  if (textType === "STORY") return "📖";
  if (textType === "DIALOGUE") return "💬";
  if (textType === "CRIME") return "🕵️";
  if (textType === "PODCAST") return "🎙️";
  return "🧠";
}

function resolveCharacterIcon(index: number) {
  if (index === 0) return "🗣️";
  if (index === 1) return "🧳";
  if (index === 2) return "🎓";
  if (index === 3) return "🏡";
  return "🧭";
}

function resolveLanguageBadge(languageId: string) {
  const language = TEXT_LANGUAGES.find((entry) => entry.id === languageId);
  if (!language?.short) {
    return "A";
  }
  const normalized = language.short.replace(/\s*\(.*\)\s*/g, "").trim();
  return normalized.slice(0, 2).toUpperCase();
}
